import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
	Select,
	MenuItem,
	Checkbox,
	FormControlLabel,
	Accordion,
	AccordionSummary,
	AccordionDetails
} from '@mui/material';
import Tooltip from '../../lib/tooltip/Tooltip';
import Button from '../../lib/button/Button';
import { getAccessToken } from '../../auth';
import { envConfig } from '../../config';
import { InfrastructureResourceType } from '@elyx-code/definitions';

import DatabaseIcon from '../../assets/relational-database.png';
import HttpEndpointIcon from '../../assets/http-endpoint.svg';
import ServerIcon from '../../assets/server.svg';
import ContainerComputeIcon from '../../assets/container-compute.svg';
import SharedDriveIcon from '../../assets/shared-drive.svg';
import ResourcesIcon from '../../assets/resources.svg';
import { capitalizeFirstLetter } from '@elyx-code/project-logic-tree';

export interface IHierarchicalScopeBuilderProps {
	workspaceId: string;
	onChange: (scopes: string[]) => void;
}

export interface IProjectItem {
	id: string;
	name: string;
	updatedAt?: string;
}

export interface IFetchedResource {
	resourceId: string;
	resourceType: string;
	provider?: string;
	name?: string | null;
	envIdentifier?: string | null;
	details?: any;
}

export interface IEnvironmentScopeConfig {
	envId: string; // e.g. 'main'
	everything: boolean;
	actions: { read: boolean; create: boolean; update: boolean };
	resourceTypes: Record<
		string,
		{ read: boolean; create: boolean; update: boolean }
	>;
	specificResources: Record<
		string,
		{ read: boolean; create: boolean; update: boolean }
	>;
}

export interface IProjectScopeConfig {
	projectId: string;
	projectName: string;
	// All Environments (*) level configuration
	everything: boolean;
	actions: { read: boolean; create: boolean; update: boolean };
	resourceTypes: Record<
		string,
		{ read: boolean; create: boolean; update: boolean }
	>;
	specificResources: Record<
		string,
		{ read: boolean; create: boolean; update: boolean }
	>;
	// Specific Environment level configurations (e.g. 'main')
	envConfigs: IEnvironmentScopeConfig[];
}

export const resolveResourceIconFromType = (resourceType: string): string => {
	switch (resourceType) {
		case InfrastructureResourceType.RelationalDatabase:
		case 'relational-database':
			return DatabaseIcon;
		case InfrastructureResourceType.HttpEndpoint:
		case InfrastructureResourceType.HttpApi:
		case 'http-endpoint':
		case 'http-api':
			return HttpEndpointIcon;
		case InfrastructureResourceType.ServerlessFunction:
		case 'serverless-function':
			return ServerIcon;
		case InfrastructureResourceType.ContainerCompute:
		case 'container-compute':
			return ContainerComputeIcon;
		case InfrastructureResourceType.SharedDrive:
		case 'shared-drive':
		case 'cloud-file-system-bucket':
			return SharedDriveIcon;
		default:
			return ResourcesIcon;
	}
};

export const RESOURCE_TYPE_LABELS: Record<
	string,
	{ label: string; iconAsset: string }
> = {
	[InfrastructureResourceType.RelationalDatabase]: {
		label: 'Relational Databases',
		iconAsset: DatabaseIcon
	},
	[InfrastructureResourceType.SharedDrive]: {
		label: 'Cloud File Systems / Shared Drives',
		iconAsset: SharedDriveIcon
	},
	[InfrastructureResourceType.ServerlessFunction]: {
		label: 'Serverless Functions',
		iconAsset: ServerIcon
	},
	[InfrastructureResourceType.HttpEndpoint]: {
		label: 'HTTP Endpoints / APIs',
		iconAsset: HttpEndpointIcon
	},
	[InfrastructureResourceType.ContainerCompute]: {
		label: 'Container Compute',
		iconAsset: ContainerComputeIcon
	},
	[InfrastructureResourceType.CloudHostedCodebase]: {
		label: 'Cloud Hosted Codebase',
		iconAsset: ResourcesIcon
	}
};

export const AVAILABLE_ENVIRONMENTS = [
	{ id: 'main', label: 'main (Default Environment)' }
];

export const HierarchicalScopeBuilder: React.FC<
	IHierarchicalScopeBuilderProps
> = ({ workspaceId, onChange }) => {
	// 1. Workspace Level State
	const [workspaceActions, setWorkspaceActions] = useState({
		read: false,
		create: false,
		update: false
	});

	const [workspaceResourceTypes, setWorkspaceResourceTypes] = useState<
		Record<string, { read: boolean; create: boolean; update: boolean }>
	>({
		[InfrastructureResourceType.RelationalDatabase]: {
			read: false,
			create: false,
			update: false
		},
		[InfrastructureResourceType.SharedDrive]: {
			read: false,
			create: false,
			update: false
		},
		[InfrastructureResourceType.ServerlessFunction]: {
			read: false,
			create: false,
			update: false
		},
		[InfrastructureResourceType.HttpEndpoint]: {
			read: false,
			create: false,
			update: false
		},
		[InfrastructureResourceType.ContainerCompute]: {
			read: false,
			create: false,
			update: false
		},
		[InfrastructureResourceType.CloudHostedCodebase]: {
			read: false,
			create: false,
			update: false
		}
	});

	// 2. Project List & Specific Projects Config State
	const [availableProjects, setAvailableProjects] = useState<IProjectItem[]>(
		[]
	);
	const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(false);
	const [selectedProjectToAdd, setSelectedProjectToAdd] =
		useState<string>('');
	const [projectConfigs, setProjectConfigs] = useState<IProjectScopeConfig[]>(
		[]
	);
	const [expandedProjects, setExpandedProjects] = useState<
		Record<string, boolean>
	>({});

	// Selected Environment to add per project (key: projectId -> envId)
	const [selectedEnvToAdd, setSelectedEnvToAdd] = useState<
		Record<string, string>
	>({});
	const [expandedEnvs, setExpandedEnvs] = useState<Record<string, boolean>>(
		{}
	); // key: `${projectId}:${envId}`

	// Fetched Infrastructure Resources map (projectId -> IFetchedResource[])
	const [projectResources, setProjectResources] = useState<
		Record<string, IFetchedResource[]>
	>({});
	const [isLoadingResources, setIsLoadingResources] = useState<
		Record<string, boolean>
	>({});

	// Expandable Resource Type Sections (key: `${projectId}:${resType}` or `${projectId}:${envId}:${resType}`)
	const [expandedResourceTypes, setExpandedResourceTypes] = useState<
		Record<string, boolean>
	>({});

	// Fetch workspace projects
	const fetchProjects = useCallback(async () => {
		if (!workspaceId) return;
		setIsLoadingProjects(true);
		try {
			const token = await getAccessToken();
			const res = await axios.get(
				`${envConfig.API_BASE_URL}/editor-service/v1/projects?workspaceId=${workspaceId}&limit=100`,
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			);
			const rawList =
				res.data?.items ||
				res.data?.data?.items ||
				res.data?.data?.projects ||
				res.data?.data ||
				[];
			const formattedList: IProjectItem[] = rawList
				.filter((p: any) => !p.deleted)
				.map((p: any) => ({
					id: p.id,
					name: p.name || 'Untitled Project',
					updatedAt: p.updatedAt
				}));

			setAvailableProjects(formattedList);
		} catch (err) {
			console.error(
				'Failed to load workspace projects for scope builder',
				err
			);
		} finally {
			setIsLoadingProjects(false);
		}
	}, [workspaceId]);

	useEffect(() => {
		fetchProjects();
	}, [fetchProjects]);

	// Fetch specific project infrastructure resources
	const fetchResourcesForProject = useCallback(
		async (projectId: string) => {
			if (!projectId || projectResources[projectId]) return;
			setIsLoadingResources((prev) => ({ ...prev, [projectId]: true }));
			try {
				const token = await getAccessToken();
				const res = await axios.get(
					`${envConfig.API_BASE_URL}/publishing-service/v1/resources/${projectId}`,
					{
						headers: { Authorization: `Bearer ${token}` }
					}
				);
				const resourcesList: IFetchedResource[] =
					res.data?.data?.resources || res.data?.resources || [];
				setProjectResources((prev) => ({
					...prev,
					[projectId]: resourcesList
				}));
			} catch (err) {
				console.error(
					`Failed to fetch infrastructure resources for project ${projectId}`,
					err
				);
				setProjectResources((prev) => ({ ...prev, [projectId]: [] }));
			} finally {
				setIsLoadingResources((prev) => ({
					...prev,
					[projectId]: false
				}));
			}
		},
		[projectResources]
	);

	// Handlers for Workspace Actions
	const handleWorkspaceActionToggle = (
		action: 'read' | 'create' | 'update'
	) => {
		setWorkspaceActions((prev) => ({ ...prev, [action]: !prev[action] }));
	};

	const handleWorkspaceResourceTypeActionToggle = (
		resType: string,
		action: 'read' | 'create' | 'update'
	) => {
		setWorkspaceResourceTypes((prev) => ({
			...prev,
			[resType]: {
				...prev[resType],
				[action]: !prev[resType]?.[action]
			}
		}));
	};

	// Add Project Scope Card
	const handleAddProjectConfig = () => {
		if (!selectedProjectToAdd) return;
		const proj = availableProjects.find(
			(p) => p.id === selectedProjectToAdd
		);
		if (!proj) return;

		if (projectConfigs.some((pc) => pc.projectId === proj.id)) return;

		const newConfig: IProjectScopeConfig = {
			projectId: proj.id,
			projectName: proj.name,
			everything: false,
			actions: { read: false, create: false, update: false },
			resourceTypes: {
				[InfrastructureResourceType.RelationalDatabase]: {
					read: false,
					create: false,
					update: false
				},
				[InfrastructureResourceType.SharedDrive]: {
					read: false,
					create: false,
					update: false
				},
				[InfrastructureResourceType.ServerlessFunction]: {
					read: false,
					create: false,
					update: false
				},
				[InfrastructureResourceType.HttpEndpoint]: {
					read: false,
					create: false,
					update: false
				},
				[InfrastructureResourceType.ContainerCompute]: {
					read: false,
					create: false,
					update: false
				},
				[InfrastructureResourceType.CloudHostedCodebase]: {
					read: false,
					create: false,
					update: false
				}
			},
			specificResources: {},
			envConfigs: []
		};

		setProjectConfigs((prev) => [...prev, newConfig]);
		setExpandedProjects((prev) => ({ ...prev, [proj.id]: true }));
		setSelectedProjectToAdd('');
		fetchResourcesForProject(proj.id);
	};

	const handleRemoveProjectConfig = (projectId: string) => {
		setProjectConfigs((prev) =>
			prev.filter((p) => p.projectId !== projectId)
		);
	};

	// Environment Add Handler per Project
	const handleAddEnvConfig = (projectId: string) => {
		const envId = selectedEnvToAdd[projectId];
		if (!envId) return;

		setProjectConfigs((prev) =>
			prev.map((pc) => {
				if (pc.projectId !== projectId) return pc;
				if (pc.envConfigs.some((ec) => ec.envId === envId)) return pc;

				const newEnvConfig: IEnvironmentScopeConfig = {
					envId,
					everything: false,
					actions: { read: false, create: false, update: false },
					resourceTypes: {
						[InfrastructureResourceType.RelationalDatabase]: {
							read: false,
							create: false,
							update: false
						},
						[InfrastructureResourceType.SharedDrive]: {
							read: false,
							create: false,
							update: false
						},
						[InfrastructureResourceType.ServerlessFunction]: {
							read: false,
							create: false,
							update: false
						},
						[InfrastructureResourceType.HttpEndpoint]: {
							read: false,
							create: false,
							update: false
						},
						[InfrastructureResourceType.ContainerCompute]: {
							read: false,
							create: false,
							update: false
						},
						[InfrastructureResourceType.CloudHostedCodebase]: {
							read: false,
							create: false,
							update: false
						}
					},
					specificResources: {}
				};

				return { ...pc, envConfigs: [...pc.envConfigs, newEnvConfig] };
			})
		);

		setExpandedEnvs((prev) => ({
			...prev,
			[`${projectId}:${envId}`]: true
		}));
		setSelectedEnvToAdd((prev) => ({ ...prev, [projectId]: '' }));
	};

	const handleRemoveEnvConfig = (projectId: string, envId: string) => {
		setProjectConfigs((prev) =>
			prev.map((pc) =>
				pc.projectId === projectId
					? {
							...pc,
							envConfigs: pc.envConfigs.filter(
								(ec) => ec.envId !== envId
							)
						}
					: pc
			)
		);
	};

	const toggleAccordion = (projectId: string) => {
		setExpandedProjects((prev) => ({
			...prev,
			[projectId]: !prev[projectId]
		}));
	};

	const toggleEnvAccordion = (projectId: string, envId: string) => {
		const key = `${projectId}:${envId}`;
		setExpandedEnvs((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const toggleResourceTypeExpand = (key: string) => {
		setExpandedResourceTypes((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	// Subsumption Checker Helpers
	const isWorkspaceProjectActionActive = (
		action: 'read' | 'create' | 'update'
	): boolean => {
		return workspaceActions[action];
	};

	const isWorkspaceResourceTypeActionActive = (
		resType: string,
		action: 'read' | 'create' | 'update'
	): boolean => {
		const typeConfig = workspaceResourceTypes[resType];
		return !!typeConfig?.[action];
	};

	// Compute compiled scopes array
	const compiledScopes = useMemo<string[]>(() => {
		const scopes: string[] = [];

		// Workspace project actions
		if (workspaceActions.read) scopes.push('project:read');
		if (workspaceActions.create) scopes.push('project:create');
		if (workspaceActions.update) scopes.push('project:update');

		// Workspace resource types
		Object.entries(workspaceResourceTypes).forEach(([resType, actions]) => {
			if (actions.read) scopes.push(`project:*:${resType}:read`);
			if (actions.create) scopes.push(`project:*:${resType}:create`);
			if (actions.update) scopes.push(`project:*:${resType}:update`);
		});

		// Project level configs (All Envs '*')
		projectConfigs.forEach((pc) => {
			if (pc.everything) {
				scopes.push(`project:${pc.projectId}:*`);
			} else {
				if (pc.actions.read && !workspaceActions.read) {
					scopes.push(`project:${pc.projectId}:read`);
				}
				if (pc.actions.create && !workspaceActions.create) {
					scopes.push(`project:${pc.projectId}:create`);
				}
				if (pc.actions.update && !workspaceActions.update) {
					scopes.push(`project:${pc.projectId}:update`);
				}

				Object.entries(pc.resourceTypes).forEach(
					([resType, actions]) => {
						const wsTypeConfig = workspaceResourceTypes[resType];
						if (actions.read && !wsTypeConfig?.read) {
							scopes.push(
								`project:${pc.projectId}:${resType}:read`
							);
						}
						if (actions.create && !wsTypeConfig?.create) {
							scopes.push(
								`project:${pc.projectId}:${resType}:create`
							);
						}
						if (actions.update && !wsTypeConfig?.update) {
							scopes.push(
								`project:${pc.projectId}:${resType}:update`
							);
						}
					}
				);

				Object.entries(pc.specificResources).forEach(
					([resId, actions]) => {
						if (actions.read)
							scopes.push(
								`project:${pc.projectId}:resource:${resId}:read`
							);
						if (actions.create)
							scopes.push(
								`project:${pc.projectId}:resource:${resId}:create`
							);
						if (actions.update)
							scopes.push(
								`project:${pc.projectId}:resource:${resId}:update`
							);
					}
				);
			}

			// Specific Environment level configs (e.g. env:main)
			pc.envConfigs.forEach((ec) => {
				const envPrefix = `:env:${ec.envId}`;

				if (ec.everything && !pc.everything) {
					scopes.push(`project:${pc.projectId}${envPrefix}:*`);
				} else if (!pc.everything && !ec.everything) {
					if (
						ec.actions.read &&
						!workspaceActions.read &&
						!pc.actions.read
					) {
						scopes.push(`project:${pc.projectId}${envPrefix}:read`);
					}
					if (
						ec.actions.create &&
						!workspaceActions.create &&
						!pc.actions.create
					) {
						scopes.push(
							`project:${pc.projectId}${envPrefix}:create`
						);
					}
					if (
						ec.actions.update &&
						!workspaceActions.update &&
						!pc.actions.update
					) {
						scopes.push(
							`project:${pc.projectId}${envPrefix}:update`
						);
					}

					Object.entries(ec.resourceTypes).forEach(
						([resType, actions]) => {
							const wsTypeConfig =
								workspaceResourceTypes[resType];
							const pcTypeConfig = pc.resourceTypes[resType];

							if (
								actions.read &&
								!wsTypeConfig?.read &&
								!pcTypeConfig?.read
							) {
								scopes.push(
									`project:${pc.projectId}${envPrefix}:${resType}:read`
								);
							}
							if (
								actions.create &&
								!wsTypeConfig?.create &&
								!pcTypeConfig?.create
							) {
								scopes.push(
									`project:${pc.projectId}${envPrefix}:${resType}:create`
								);
							}
							if (
								actions.update &&
								!wsTypeConfig?.update &&
								!pcTypeConfig?.update
							) {
								scopes.push(
									`project:${pc.projectId}${envPrefix}:${resType}:update`
								);
							}
						}
					);

					Object.entries(ec.specificResources).forEach(
						([resId, actions]) => {
							if (actions.read)
								scopes.push(
									`project:${pc.projectId}${envPrefix}:resource:${resId}:read`
								);
							if (actions.create)
								scopes.push(
									`project:${pc.projectId}${envPrefix}:resource:${resId}:create`
								);
							if (actions.update)
								scopes.push(
									`project:${pc.projectId}${envPrefix}:resource:${resId}:update`
								);
						}
					);
				}
			});
		});

		return scopes.length === 0 ? ['*'] : scopes;
	}, [workspaceActions, workspaceResourceTypes, projectConfigs]);

	// Notify parent component whenever compiled scopes change
	useEffect(() => {
		onChange(compiledScopes);
	}, [compiledScopes, onChange]);

	const remainingProjects = availableProjects.filter(
		(ap) => !projectConfigs.some((pc) => pc.projectId === ap.id)
	);

	// Helper to match fetched resourceType string to enum key
	const isResourceOfType = (
		res: IFetchedResource,
		resType: string
	): boolean => {
		if (res.resourceType === resType) return true;
		if (
			resType === InfrastructureResourceType.SharedDrive &&
			res.resourceType === 'cloud-file-system-bucket'
		)
			return true;
		if (
			resType === InfrastructureResourceType.HttpEndpoint &&
			res.resourceType === 'http-api'
		)
			return true;
		return false;
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
				border: '1px solid #e2e8f0',
				borderRadius: '8px',
				padding: '16px',
				backgroundColor: '#f8fafc',
				maxHeight: '380px',
				overflowY: 'auto'
			}}
		>
			{/* TIER 1: WORKSPACE-WIDE SCOPES */}
			<div
				style={{
					backgroundColor: 'white',
					border: '1px solid #cbd5e1',
					borderRadius: '8px',
					padding: '14px',
					display: 'flex',
					flexDirection: 'column',
					gap: '12px'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px'
					}}
				>
					<i
						className="fa-solid fa-layer-group"
						style={{ color: '#4f46e5', fontSize: '15px' }}
					/>
					<span
						style={{
							fontSize: '13.5px',
							fontWeight: 600,
							color: '#1e293b'
						}}
					>
						Workspace-Wide Permissions (All Projects & Resources)
					</span>
				</div>

				{/* 1.A Project Actions */}
				<div>
					<span
						style={{
							fontSize: '12px',
							fontWeight: 600,
							color: '#64748b'
						}}
					>
						Project Management Actions
					</span>
					<div
						style={{
							display: 'flex',
							gap: '16px',
							marginTop: '4px'
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={workspaceActions.read}
									onChange={() =>
										handleWorkspaceActionToggle('read')
									}
								/>
							}
							label={
								<span
									style={{
										fontSize: '13px',
										color: '#334155'
									}}
								>
									Read (View Projects)
								</span>
							}
						/>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={workspaceActions.create}
									onChange={() =>
										handleWorkspaceActionToggle('create')
									}
								/>
							}
							label={
								<span
									style={{
										fontSize: '13px',
										color: '#334155'
									}}
								>
									Create (New Projects)
								</span>
							}
						/>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={workspaceActions.update}
									onChange={() =>
										handleWorkspaceActionToggle('update')
									}
								/>
							}
							label={
								<span
									style={{
										fontSize: '13px',
										color: '#334155'
									}}
								>
									Update (Modify Projects)
								</span>
							}
						/>
					</div>
				</div>

				{/* 1.B Infrastructure Resource Types */}
				<div
					style={{
						borderTop: '1px solid #f1f5f9',
						paddingTop: '10px'
					}}
				>
					<span
						style={{
							fontSize: '12px',
							fontWeight: 600,
							color: '#64748b'
						}}
					>
						Infrastructure Resource Types (Workspace-Wide)
					</span>

					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							marginTop: '6px'
						}}
					>
						{Object.entries(RESOURCE_TYPE_LABELS).map(
							([resType, info]) => {
								const current = workspaceResourceTypes[
									resType
								] || {
									read: false,
									create: false,
									update: false
								};

								return (
									<div
										key={resType}
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '6px 10px',
											backgroundColor: '#f8fafc',
											borderRadius: '6px',
											border: '1px solid #e2e8f0'
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '10px'
											}}
										>
											<img
												src={info.iconAsset}
												alt={info.label}
												style={{
													width: '18px',
													height: '18px',
													objectFit: 'contain'
												}}
											/>
											<span
												style={{
													fontSize: '12.5px',
													fontWeight: 500,
													color: '#334155'
												}}
											>
												{info.label}
											</span>
										</div>

										<div
											style={{
												display: 'flex',
												gap: '12px'
											}}
										>
											<FormControlLabel
												control={
													<Checkbox
														size="small"
														checked={current.read}
														onChange={() =>
															handleWorkspaceResourceTypeActionToggle(
																resType,
																'read'
															)
														}
													/>
												}
												label={
													<span
														style={{
															fontSize: '12px'
														}}
													>
														Read
													</span>
												}
											/>
											<FormControlLabel
												control={
													<Checkbox
														size="small"
														checked={current.create}
														onChange={() =>
															handleWorkspaceResourceTypeActionToggle(
																resType,
																'create'
															)
														}
													/>
												}
												label={
													<span
														style={{
															fontSize: '12px'
														}}
													>
														Create
													</span>
												}
											/>
											<FormControlLabel
												control={
													<Checkbox
														size="small"
														checked={current.update}
														onChange={() =>
															handleWorkspaceResourceTypeActionToggle(
																resType,
																'update'
															)
														}
													/>
												}
												label={
													<span
														style={{
															fontSize: '12px'
														}}
													>
														Update
													</span>
												}
											/>
										</div>
									</div>
								);
							}
						)}
					</div>
				</div>
			</div>

			{/* TIER 2: SPECIFIC PROJECTS SCOPES (DYNAMIC & COLLAPSIBLE) */}
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: '10px'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between'
					}}
				>
					<span
						style={{
							fontSize: '13px',
							fontWeight: 600,
							color: '#1e293b'
						}}
					>
						Project-Specific Scopes
					</span>
					{isLoadingProjects && (
						<span style={{ fontSize: '11.5px', color: '#64748b' }}>
							Loading projects...
						</span>
					)}
				</div>

				{/* Project Add Controls */}
				<div
					style={{
						display: 'flex',
						gap: '10px',
						alignItems: 'center'
					}}
				>
					<Select
						value={selectedProjectToAdd}
						onChange={(e) =>
							setSelectedProjectToAdd(e.target.value as string)
						}
						size="small"
						displayEmpty
						disabled={
							isLoadingProjects ||
							availableProjects.length === 0 ||
							remainingProjects.length === 0
						}
						style={{
							flex: 1,
							backgroundColor: 'white',
							fontSize: '13px'
						}}
					>
						<MenuItem value="" disabled>
							{isLoadingProjects
								? 'Loading workspace projects...'
								: remainingProjects.length === 0
									? 'All workspace projects added'
									: 'Select a project to configure...'}
						</MenuItem>
						{remainingProjects.map((p) => (
							<MenuItem
								key={p.id}
								value={p.id}
								style={{ fontSize: '13px' }}
							>
								<i
									className="fa-solid fa-diagram-project"
									style={{
										marginRight: '8px',
										color: '#6366f1'
									}}
								/>
								{p.name}
							</MenuItem>
						))}
					</Select>

					<Button
						variant="outlined"
						color="primary"
						size="small"
						iconStart="fa-solid fa-plus"
						disabled={!selectedProjectToAdd}
						onClick={handleAddProjectConfig}
					>
						Add Project Scope
					</Button>
				</div>

				{/* Collapsible Project Scope Cards */}
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '8px'
					}}
				>
					{projectConfigs.map((pc) => {
						const isExpanded =
							expandedProjects[pc.projectId] ?? true;
						const resources = projectResources[pc.projectId] || [];
						const isLoadingRes = isLoadingResources[pc.projectId];
						const selectedEnvForProj =
							selectedEnvToAdd[pc.projectId] || '';
						const unaddedEnvs = AVAILABLE_ENVIRONMENTS.filter(
							(e) =>
								!pc.envConfigs.some((ec) => ec.envId === e.id)
						);

						return (
							<Accordion
								key={pc.projectId}
								expanded={isExpanded}
								onChange={() => toggleAccordion(pc.projectId)}
								style={{
									border: '1px solid #cbd5e1',
									borderRadius: '8px',
									boxShadow: 'none',
									overflow: 'hidden'
								}}
							>
								<AccordionSummary
									expandIcon={
										<i
											className="fa-solid fa-chevron-down"
											style={{ fontSize: '11px' }}
										/>
									}
									style={{
										backgroundColor: '#f1f5f9',
										minHeight: '34px',
										maxHeight: '34px',
										padding: '0 10px'
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											width: '100%',
											paddingRight: '6px'
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px'
											}}
										>
											<i
												className="fa-solid fa-folder-tree"
												style={{
													color: '#4f46e5',
													fontSize: '12.5px'
												}}
											/>
											<span
												style={{
													fontSize: '12.5px',
													fontWeight: 600,
													color: '#0f172a'
												}}
											>
												{pc.projectName}
											</span>
										</div>

										<Button
											variant="text"
											color="error"
											size="small"
											iconStart="fa-solid fa-trash-can"
											onClick={(e: React.MouseEvent) => {
												e.stopPropagation();
												handleRemoveProjectConfig(
													pc.projectId
												);
											}}
										>
											Remove
										</Button>
									</div>
								</AccordionSummary>

								<AccordionDetails
									style={{
										padding: '12px',
										display: 'flex',
										flexDirection: 'column',
										gap: '14px'
									}}
								>
									{/* All Environments (*) Scope Section */}
									<div
										style={{
											backgroundColor: '#ffffff',
											border: '1px solid #e2e8f0',
											borderRadius: '6px',
											padding: '10px'
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												marginBottom: '8px'
											}}
										>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px'
												}}
											>
												<i
													className="fa-solid fa-globe"
													style={{
														color: '#2563eb',
														fontSize: '13px'
													}}
												/>
												<span
													style={{
														fontSize: '12.5px',
														fontWeight: 600,
														color: '#1e293b'
													}}
												>
													All Environments (*)
													Permissions
												</span>
											</div>
										</div>

										{/* Project Level Actions (All Envs) */}
										<div>
											<span
												style={{
													fontSize: '11.5px',
													fontWeight: 600,
													color: '#64748b'
												}}
											>
												Project Actions
											</span>
											<div
												style={{
													display: 'flex',
													gap: '14px',
													marginTop: '2px',
													flexWrap: 'wrap'
												}}
											>
												{/* Everything (*) Checkbox */}
												<FormControlLabel
													control={
														<Checkbox
															size="small"
															checked={
																pc.everything
															}
															color="primary"
															onChange={(e) => {
																const checked =
																	e.target
																		.checked;
																setProjectConfigs(
																	(prev) =>
																		prev.map(
																			(
																				item
																			) =>
																				item.projectId ===
																				pc.projectId
																					? {
																							...item,
																							everything:
																								checked
																						}
																					: item
																		)
																);
															}}
														/>
													}
													label={
														<span
															style={{
																fontSize:
																	'12px',
																fontWeight: 600,
																color: '#4f46e5'
															}}
														>
															Everything (*)
														</span>
													}
												/>

												{(
													[
														'read',
														'create',
														'update'
													] as const
												).map((act) => {
													const isSubsumed =
														pc.everything ||
														isWorkspaceProjectActionActive(
															act
														);

													return (
														<span
															key={act}
															id={`subsumed-project-${pc.projectId}-${act}`}
														>
															<FormControlLabel
																control={
																	<Checkbox
																		size="small"
																		checked={
																			isSubsumed ||
																			pc
																				.actions[
																				act
																			]
																		}
																		disabled={
																			isSubsumed
																		}
																		onChange={(
																			e
																		) => {
																			const checked =
																				e
																					.target
																					.checked;
																			setProjectConfigs(
																				(
																					prev
																				) =>
																					prev.map(
																						(
																							item
																						) =>
																							item.projectId ===
																							pc.projectId
																								? {
																										...item,
																										actions:
																											{
																												...item.actions,
																												[act]: checked
																											}
																									}
																								: item
																					)
																			);
																		}}
																	/>
																}
																label={
																	<span
																		style={{
																			fontSize:
																				'12px',
																			color: isSubsumed
																				? '#94a3b8'
																				: '#334155'
																		}}
																	>
																		{capitalizeFirstLetter(
																			act
																		)}
																	</span>
																}
															/>
															{isSubsumed && (
																<Tooltip
																	anchorQuerySelector={`#subsumed-project-${pc.projectId}-${act}`}
																	placement="top"
																>
																	Covered by
																	Project
																	Everything
																	(*) or
																	Workspace
																	scope.
																</Tooltip>
															)}
														</span>
													);
												})}
											</div>
										</div>

										{/* Resource Types inside Project (All Envs) */}
										<div
											style={{
												borderTop: '1px solid #f1f5f9',
												paddingTop: '8px',
												marginTop: '8px'
											}}
										>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent:
														'space-between'
												}}
											>
												<span
													style={{
														fontSize: '11.5px',
														fontWeight: 600,
														color: '#64748b'
													}}
												>
													Infrastructure Resource
													Types
												</span>
												{isLoadingRes && (
													<span
														style={{
															fontSize: '10.5px',
															color: '#64748b'
														}}
													>
														Fetching resources...
													</span>
												)}
											</div>

											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													gap: '6px',
													marginTop: '6px'
												}}
											>
												{Object.entries(
													RESOURCE_TYPE_LABELS
												).map(([resType, info]) => {
													const currentTypeConfig = pc
														.resourceTypes[
														resType
													] || {
														read: false,
														create: false,
														update: false
													};

													const expandKey = `${pc.projectId}:all:${resType}`;
													const isResTypeExpanded =
														expandedResourceTypes[
															expandKey
														] ?? false;

													const matchingResources =
														resources.filter(
															(res) =>
																isResourceOfType(
																	res,
																	resType
																)
														);

													return (
														<div
															key={resType}
															style={{
																display: 'flex',
																flexDirection:
																	'column',
																backgroundColor:
																	'#f8fafc',
																borderRadius:
																	'6px',
																border: '1px solid #cbd5e1',
																overflow:
																	'hidden'
															}}
														>
															<div
																style={{
																	display:
																		'flex',
																	alignItems:
																		'center',
																	justifyContent:
																		'space-between',
																	padding:
																		'4px 8px',
																	cursor:
																		matchingResources.length >
																		0
																			? 'pointer'
																			: 'default'
																}}
																onClick={() => {
																	if (
																		matchingResources.length >
																		0
																	) {
																		toggleResourceTypeExpand(
																			expandKey
																		);
																	}
																}}
															>
																<div
																	style={{
																		display:
																			'flex',
																		alignItems:
																			'center',
																		gap: '8px'
																	}}
																>
																	{matchingResources.length >
																		0 && (
																		<i
																			className={`fa-solid ${
																				isResTypeExpanded
																					? 'fa-chevron-down'
																					: 'fa-chevron-right'
																			}`}
																			style={{
																				fontSize:
																					'10px',
																				color: '#64748b'
																			}}
																		/>
																	)}
																	<img
																		src={
																			info.iconAsset
																		}
																		alt={
																			info.label
																		}
																		style={{
																			width: '16px',
																			height: '16px',
																			objectFit:
																				'contain'
																		}}
																	/>
																	<span
																		style={{
																			fontSize:
																				'12px',
																			fontWeight: 500,
																			color: '#1e293b'
																		}}
																	>
																		{
																			info.label
																		}
																	</span>
																	{matchingResources.length >
																		0 && (
																		<span
																			style={{
																				fontSize:
																					'10.5px',
																				backgroundColor:
																					'#e0e7ff',
																				color: '#4338ca',
																				padding:
																					'1px 6px',
																				borderRadius:
																					'10px',
																				fontWeight: 600
																			}}
																		>
																			{
																				matchingResources.length
																			}
																		</span>
																	)}
																</div>

																<div
																	style={{
																		display:
																			'flex',
																		gap: '10px'
																	}}
																	onClick={(
																		e
																	) =>
																		e.stopPropagation()
																	}
																>
																	{(
																		[
																			'read',
																			'create',
																			'update'
																		] as const
																	).map(
																		(
																			act
																		) => {
																			const isSubsumed =
																				pc.everything ||
																				isWorkspaceResourceTypeActionActive(
																					resType,
																					act
																				);

																			return (
																				<span
																					key={
																						act
																					}
																					id={`subsumed-res-${pc.projectId}-${resType}-${act}`}
																				>
																					<FormControlLabel
																						control={
																							<Checkbox
																								size="small"
																								checked={
																									isSubsumed ||
																									currentTypeConfig[
																										act
																									]
																								}
																								disabled={
																									isSubsumed
																								}
																								onChange={(
																									e
																								) => {
																									const checked =
																										e
																											.target
																											.checked;
																									setProjectConfigs(
																										(
																											prev
																										) =>
																											prev.map(
																												(
																													item
																												) =>
																													item.projectId ===
																													pc.projectId
																														? {
																																...item,
																																resourceTypes:
																																	{
																																		...item.resourceTypes,
																																		[resType]:
																																			{
																																				...item
																																					.resourceTypes[
																																					resType
																																				],
																																				[act]: checked
																																			}
																																	}
																															}
																														: item
																											)
																									);
																								}}
																							/>
																						}
																						label={
																							<span
																								style={{
																									fontSize:
																										'11.5px',
																									color: isSubsumed
																										? '#94a3b8'
																										: '#334155'
																								}}
																							>
																								{capitalizeFirstLetter(
																									act
																								)}
																							</span>
																						}
																					/>
																					{isSubsumed && (
																						<Tooltip
																							anchorQuerySelector={`#subsumed-res-${pc.projectId}-${resType}-${act}`}
																							placement="top"
																						>
																							Covered
																							by
																							Project
																							Everything
																							(*)
																							or
																							Workspace
																							scope.
																						</Tooltip>
																					)}
																				</span>
																			);
																		}
																	)}
																</div>
															</div>

															{isResTypeExpanded &&
																matchingResources.length >
																	0 && (
																	<div
																		style={{
																			borderTop:
																				'1px dashed #cbd5e1',
																			backgroundColor:
																				'white',
																			padding:
																				'8px 12px',
																			display:
																				'flex',
																			flexDirection:
																				'column',
																			gap: '6px'
																		}}
																	>
																		<span
																			style={{
																				fontSize:
																					'11px',
																				fontWeight: 600,
																				color: '#64748b'
																			}}
																		>
																			Specific
																			Resource
																			Items:
																		</span>

																		{matchingResources.map(
																			(
																				resItem
																			) => {
																				const displayName =
																					resItem.name ||
																					resItem
																						.details
																						?.databaseName ||
																					resItem
																						.details
																						?.functionName ||
																					resItem
																						.details
																						?.bucketName ||
																					resItem.resourceId;

																				const itemConfig =
																					pc
																						.specificResources[
																						resItem
																							.resourceId
																					] || {
																						read: false,
																						create: false,
																						update: false
																					};

																				return (
																					<div
																						key={
																							resItem.resourceId
																						}
																						style={{
																							display:
																								'flex',
																							alignItems:
																								'center',
																							justifyContent:
																								'space-between',
																							padding:
																								'4px 8px',
																							backgroundColor:
																								'#f8fafc',
																							borderRadius:
																								'4px',
																							border: '1px solid #f1f5f9'
																						}}
																					>
																						<div
																							style={{
																								display:
																									'flex',
																								alignItems:
																									'center',
																								gap: '6px'
																							}}
																						>
																							<i
																								className="fa-solid fa-cube"
																								style={{
																									fontSize:
																										'11px',
																									color: '#6366f1'
																								}}
																							/>
																							<span
																								style={{
																									fontSize:
																										'11.5px',
																									color: '#1e293b',
																									fontWeight: 500
																								}}
																							>
																								{
																									displayName
																								}
																							</span>
																						</div>

																						<div
																							style={{
																								display:
																									'flex',
																								gap: '8px'
																							}}
																						>
																							{(
																								[
																									'read',
																									'create',
																									'update'
																								] as const
																							).map(
																								(
																									act
																								) => {
																									const isParentSubsumed =
																										pc.everything ||
																										isWorkspaceResourceTypeActionActive(
																											resType,
																											act
																										) ||
																										currentTypeConfig[
																											act
																										];

																									return (
																										<span
																											key={
																												act
																											}
																											id={`subsumed-item-${pc.projectId}-${resItem.resourceId}-${act}`}
																										>
																											<FormControlLabel
																												control={
																													<Checkbox
																														size="small"
																														checked={
																															isParentSubsumed ||
																															itemConfig[
																																act
																															]
																														}
																														disabled={
																															isParentSubsumed
																														}
																														onChange={(
																															e
																														) => {
																															const checked =
																																e
																																	.target
																																	.checked;
																															setProjectConfigs(
																																(
																																	prev
																																) =>
																																	prev.map(
																																		(
																																			item
																																		) =>
																																			item.projectId ===
																																			pc.projectId
																																				? {
																																						...item,
																																						specificResources:
																																							{
																																								...item.specificResources,
																																								[resItem.resourceId]:
																																									{
																																										...item
																																											.specificResources[
																																											resItem
																																												.resourceId
																																										],
																																										[act]: checked
																																									}
																																							}
																																					}
																																				: item
																																	)
																															);
																														}}
																													/>
																												}
																												label={
																													<span
																														style={{
																															fontSize:
																																'11px',
																															color: isParentSubsumed
																																? '#94a3b8'
																																: '#334155'
																														}}
																													>
																														{capitalizeFirstLetter(
																															act
																														)}
																													</span>
																												}
																											/>
																											{isParentSubsumed && (
																												<Tooltip
																													anchorQuerySelector={`#subsumed-item-${pc.projectId}-${resItem.resourceId}-${act}`}
																													placement="top"
																												>
																													Covered
																													by
																													parent
																													scope.
																												</Tooltip>
																											)}
																										</span>
																									);
																								}
																							)}
																						</div>
																					</div>
																				);
																			}
																		)}
																	</div>
																)}
														</div>
													);
												})}
											</div>
										</div>
									</div>

									{/* SPECIFIC ENVIRONMENT SCOPES SECTION */}
									<div
										style={{
											borderTop: '1px solid #cbd5e1',
											paddingTop: '12px'
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												marginBottom: '8px'
											}}
										>
											<span
												style={{
													fontSize: '12.5px',
													fontWeight: 600,
													color: '#1e293b'
												}}
											>
												Specific Environment Scopes
											</span>
										</div>

										{/* Add Environment Selector & Button */}
										<div
											style={{
												display: 'flex',
												gap: '8px',
												alignItems: 'center',
												marginBottom: '8px'
											}}
										>
											<Select
												value={selectedEnvForProj}
												onChange={(e) =>
													setSelectedEnvToAdd(
														(prev) => ({
															...prev,
															[pc.projectId]: e
																.target
																.value as string
														})
													)
												}
												size="small"
												displayEmpty
												disabled={
													unaddedEnvs.length === 0
												}
												style={{
													flex: 1,
													backgroundColor: 'white',
													fontSize: '12.5px'
												}}
											>
												<MenuItem value="" disabled>
													{unaddedEnvs.length === 0
														? 'All environments configured'
														: 'Select an environment to configure...'}
												</MenuItem>
												{unaddedEnvs.map((env) => (
													<MenuItem
														key={env.id}
														value={env.id}
														style={{
															fontSize: '12.5px'
														}}
													>
														<i
															className="fa-solid fa-server"
															style={{
																marginRight:
																	'8px',
																color: '#0284c7'
															}}
														/>
														{env.label}
													</MenuItem>
												))}
											</Select>

											<Button
												variant="outlined"
												color="primary"
												size="small"
												iconStart="fa-solid fa-plus"
												disabled={!selectedEnvForProj}
												onClick={() =>
													handleAddEnvConfig(
														pc.projectId
													)
												}
											>
												Add Environment Scope
											</Button>
										</div>

										{/* Render Specific Environment Cards */}
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: '8px'
											}}
										>
											{pc.envConfigs.map((ec) => {
												const envExpandKey = `${pc.projectId}:${ec.envId}`;
												const isEnvExpanded =
													expandedEnvs[
														envExpandKey
													] ?? true;

												return (
													<Accordion
														key={ec.envId}
														expanded={isEnvExpanded}
														onChange={() =>
															toggleEnvAccordion(
																pc.projectId,
																ec.envId
															)
														}
														style={{
															border: '1px solid #bfdbfe',
															borderRadius: '6px',
															boxShadow: 'none',
															overflow: 'hidden'
														}}
													>
														<AccordionSummary
															expandIcon={
																<i
																	className="fa-solid fa-chevron-down"
																	style={{
																		fontSize:
																			'10px'
																	}}
																/>
															}
															style={{
																backgroundColor:
																	'#eff6ff',
																minHeight:
																	'32px',
																maxHeight:
																	'32px',
																padding: '0 8px'
															}}
														>
															<div
																style={{
																	display:
																		'flex',
																	alignItems:
																		'center',
																	justifyContent:
																		'space-between',
																	width: '100%',
																	paddingRight:
																		'4px'
																}}
															>
																<div
																	style={{
																		display:
																			'flex',
																		alignItems:
																			'center',
																		gap: '6px'
																	}}
																>
																	<i
																		className="fa-solid fa-server"
																		style={{
																			color: '#0284c7',
																			fontSize:
																				'11.5px'
																		}}
																	/>
																	<span
																		style={{
																			fontSize:
																				'11.5px',
																			fontWeight: 600,
																			color: '#1e3a8a'
																		}}
																	>
																		Environment:{' '}
																		{
																			ec.envId
																		}
																	</span>
																</div>

																<Button
																	variant="text"
																	color="error"
																	size="small"
																	iconStart="fa-solid fa-trash-can"
																	onClick={(
																		e: React.MouseEvent
																	) => {
																		e.stopPropagation();
																		handleRemoveEnvConfig(
																			pc.projectId,
																			ec.envId
																		);
																	}}
																>
																	Remove
																</Button>
															</div>
														</AccordionSummary>

														<AccordionDetails
															style={{
																padding: '10px',
																display: 'flex',
																flexDirection:
																	'column',
																gap: '10px'
															}}
														>
															{/* Env Actions */}
															<div>
																<span
																	style={{
																		fontSize:
																			'11.5px',
																		fontWeight: 600,
																		color: '#64748b'
																	}}
																>
																	Environment
																	Actions
																</span>
																<div
																	style={{
																		display:
																			'flex',
																		gap: '14px',
																		marginTop:
																			'2px',
																		flexWrap:
																			'wrap'
																	}}
																>
																	{/* Environment Everything (*) Checkbox */}
																	<FormControlLabel
																		control={
																			<Checkbox
																				size="small"
																				checked={
																					pc.everything ||
																					ec.everything
																				}
																				disabled={
																					pc.everything
																				}
																				color="primary"
																				onChange={(
																					e
																				) => {
																					const checked =
																						e
																							.target
																							.checked;
																					setProjectConfigs(
																						(
																							prev
																						) =>
																							prev.map(
																								(
																									item
																								) =>
																									item.projectId ===
																									pc.projectId
																										? {
																												...item,
																												envConfigs:
																													item.envConfigs.map(
																														(
																															eItem
																														) =>
																															eItem.envId ===
																															ec.envId
																																? {
																																		...eItem,
																																		everything:
																																			checked
																																	}
																																: eItem
																													)
																											}
																										: item
																							)
																					);
																				}}
																			/>
																		}
																		label={
																			<span
																				style={{
																					fontSize:
																						'11.5px',
																					fontWeight: 600,
																					color: pc.everything
																						? '#94a3b8'
																						: '#0284c7'
																				}}
																			>
																				Everything
																				(*)
																			</span>
																		}
																	/>

																	{(
																		[
																			'read',
																			'create',
																			'update'
																		] as const
																	).map(
																		(
																			act
																		) => {
																			const isSubsumed =
																				pc.everything ||
																				ec.everything ||
																				isWorkspaceProjectActionActive(
																					act
																				) ||
																				pc
																					.actions[
																					act
																				];

																			return (
																				<span
																					key={
																						act
																					}
																					id={`subsumed-env-${pc.projectId}-${ec.envId}-${act}`}
																				>
																					<FormControlLabel
																						control={
																							<Checkbox
																								size="small"
																								checked={
																									isSubsumed ||
																									ec
																										.actions[
																										act
																									]
																								}
																								disabled={
																									isSubsumed
																								}
																								onChange={(
																									e
																								) => {
																									const checked =
																										e
																											.target
																											.checked;
																									setProjectConfigs(
																										(
																											prev
																										) =>
																											prev.map(
																												(
																													item
																												) =>
																													item.projectId ===
																													pc.projectId
																														? {
																																...item,
																																envConfigs:
																																	item.envConfigs.map(
																																		(
																																			eItem
																																		) =>
																																			eItem.envId ===
																																			ec.envId
																																				? {
																																						...eItem,
																																						actions:
																																							{
																																								...eItem.actions,
																																								[act]: checked
																																							}
																																					}
																																				: eItem
																																	)
																															}
																														: item
																											)
																									);
																								}}
																							/>
																						}
																						label={
																							<span
																								style={{
																									fontSize:
																										'11.5px',
																									color: isSubsumed
																										? '#94a3b8'
																										: '#334155'
																								}}
																							>
																								{capitalizeFirstLetter(
																									act
																								)}
																							</span>
																						}
																					/>
																					{isSubsumed && (
																						<Tooltip
																							anchorQuerySelector={`#subsumed-env-${pc.projectId}-${ec.envId}-${act}`}
																							placement="top"
																						>
																							Covered
																							by
																							higher-level
																							Everything
																							(*)
																							or
																							Workspace
																							scope.
																						</Tooltip>
																					)}
																				</span>
																			);
																		}
																	)}
																</div>
															</div>

															{/* Env Resource Types */}
															<div
																style={{
																	borderTop:
																		'1px solid #e2e8f0',
																	paddingTop:
																		'8px'
																}}
															>
																<span
																	style={{
																		fontSize:
																			'11.5px',
																		fontWeight: 600,
																		color: '#64748b'
																	}}
																>
																	Environment
																	Infrastructure
																	Resources
																</span>

																<div
																	style={{
																		display:
																			'flex',
																		flexDirection:
																			'column',
																		gap: '6px',
																		marginTop:
																			'4px'
																	}}
																>
																	{Object.entries(
																		RESOURCE_TYPE_LABELS
																	).map(
																		([
																			resType,
																			info
																		]) => {
																			const currentTypeConfig =
																				ec
																					.resourceTypes[
																					resType
																				] || {
																					read: false,
																					create: false,
																					update: false
																				};

																			const envResExpandKey = `${pc.projectId}:${ec.envId}:${resType}`;
																			const isEnvResExpanded =
																				expandedResourceTypes[
																					envResExpandKey
																				] ??
																				false;

																			const matchingEnvResources =
																				resources.filter(
																					(
																						res
																					) =>
																						isResourceOfType(
																							res,
																							resType
																						) &&
																						(!res.envIdentifier ||
																							res.envIdentifier ===
																								ec.envId)
																				);

																			return (
																				<div
																					key={
																						resType
																					}
																					style={{
																						display:
																							'flex',
																						flexDirection:
																							'column',
																						backgroundColor:
																							'#f8fafc',
																						borderRadius:
																							'6px',
																						border: '1px solid #cbd5e1',
																						overflow:
																							'hidden'
																					}}
																				>
																					<div
																						style={{
																							display:
																								'flex',
																							alignItems:
																								'center',
																							justifyContent:
																								'space-between',
																							padding:
																								'4px 8px',
																							cursor:
																								matchingEnvResources.length >
																								0
																									? 'pointer'
																									: 'default'
																						}}
																						onClick={() => {
																							if (
																								matchingEnvResources.length >
																								0
																							) {
																								toggleResourceTypeExpand(
																									envResExpandKey
																								);
																							}
																						}}
																					>
																						<div
																							style={{
																								display:
																									'flex',
																								alignItems:
																									'center',
																								gap: '8px'
																							}}
																						>
																							{matchingEnvResources.length >
																								0 && (
																								<i
																									className={`fa-solid ${
																										isEnvResExpanded
																											? 'fa-chevron-down'
																											: 'fa-chevron-right'
																									}`}
																									style={{
																										fontSize:
																											'10px',
																										color: '#64748b'
																									}}
																								/>
																							)}
																							<img
																								src={
																									info.iconAsset
																								}
																								alt={
																									info.label
																								}
																								style={{
																									width: '16px',
																									height: '16px',
																									objectFit:
																										'contain'
																								}}
																							/>
																							<span
																								style={{
																									fontSize:
																										'11.5px',
																									fontWeight: 500,
																									color: '#1e293b'
																								}}
																							>
																								{
																									info.label
																								}
																							</span>
																							{matchingEnvResources.length >
																								0 && (
																								<span
																									style={{
																										fontSize:
																											'10px',
																										backgroundColor:
																											'#e0e7ff',
																										color: '#4338ca',
																										padding:
																											'1px 5px',
																										borderRadius:
																											'10px',
																										fontWeight: 600
																									}}
																								>
																									{
																										matchingEnvResources.length
																									}
																								</span>
																							)}
																						</div>

																						<div
																							style={{
																								display:
																									'flex',
																								gap: '10px'
																							}}
																							onClick={(
																								e
																							) =>
																								e.stopPropagation()
																							}
																						>
																							{(
																								[
																									'read',
																									'create',
																									'update'
																								] as const
																							).map(
																								(
																									act
																								) => {
																									const isSubsumed =
																										pc.everything ||
																										ec.everything ||
																										isWorkspaceResourceTypeActionActive(
																											resType,
																											act
																										) ||
																										pc
																											.resourceTypes[
																											resType
																										]?.[
																											act
																										];

																									return (
																										<span
																											key={
																												act
																											}
																											id={`subsumed-env-res-${pc.projectId}-${ec.envId}-${resType}-${act}`}
																										>
																											<FormControlLabel
																												control={
																													<Checkbox
																														size="small"
																														checked={
																															isSubsumed ||
																															currentTypeConfig[
																																act
																															]
																														}
																														disabled={
																															isSubsumed
																														}
																														onChange={(
																															e
																														) => {
																															const checked =
																																e
																																	.target
																																	.checked;
																															setProjectConfigs(
																																(
																																	prev
																																) =>
																																	prev.map(
																																		(
																																			item
																																		) =>
																																			item.projectId ===
																																			pc.projectId
																																				? {
																																						...item,
																																						envConfigs:
																																							item.envConfigs.map(
																																								(
																																									eItem
																																								) =>
																																									eItem.envId ===
																																									ec.envId
																																										? {
																																												...eItem,
																																												resourceTypes:
																																													{
																																														...eItem.resourceTypes,
																																														[resType]:
																																															{
																																																...eItem
																																																	.resourceTypes[
																																																	resType
																																																],
																																																[act]: checked
																																															}
																																													}
																																											}
																																										: eItem
																																							)
																																					}
																																				: item
																																	)
																															);
																														}}
																													/>
																												}
																												label={
																													<span
																														style={{
																															fontSize:
																																'11px',
																															color: isSubsumed
																																? '#94a3b8'
																																: '#334155'
																														}}
																													>
																														{capitalizeFirstLetter(
																															act
																														)}
																													</span>
																												}
																											/>
																											{isSubsumed && (
																												<Tooltip
																													anchorQuerySelector={`#subsumed-env-res-${pc.projectId}-${ec.envId}-${resType}-${act}`}
																													placement="top"
																												>
																													Covered
																													by
																													higher-level
																													Everything
																													(*)
																													or
																													Workspace
																													scope.
																												</Tooltip>
																											)}
																										</span>
																									);
																								}
																							)}
																						</div>
																					</div>

																					{isEnvResExpanded &&
																						matchingEnvResources.length >
																							0 && (
																							<div
																								style={{
																									borderTop:
																										'1px dashed #cbd5e1',
																									backgroundColor:
																										'white',
																									padding:
																										'6px 10px',
																									display:
																										'flex',
																									flexDirection:
																										'column',
																									gap: '4px'
																								}}
																							>
																								{matchingEnvResources.map(
																									(
																										resItem
																									) => {
																										const displayName =
																											resItem.name ||
																											resItem
																												.details
																												?.databaseName ||
																											resItem
																												.details
																												?.functionName ||
																											resItem
																												.details
																												?.bucketName ||
																											resItem.resourceId;

																										const itemConfig =
																											ec
																												.specificResources[
																												resItem
																													.resourceId
																											] || {
																												read: false,
																												create: false,
																												update: false
																											};

																										return (
																											<div
																												key={
																													resItem.resourceId
																												}
																												style={{
																													display:
																														'flex',
																													alignItems:
																														'center',
																													justifyContent:
																														'space-between',
																													padding:
																														'4px 6px',
																													backgroundColor:
																														'#f8fafc',
																													borderRadius:
																														'4px'
																												}}
																											>
																												<div
																													style={{
																														display:
																															'flex',
																														alignItems:
																															'center',
																														gap: '6px'
																													}}
																												>
																													<i
																														className="fa-solid fa-cube"
																														style={{
																															fontSize:
																																'10px',
																															color: '#6366f1'
																														}}
																													/>
																													<span
																														style={{
																															fontSize:
																																'11px',
																															color: '#1e293b'
																														}}
																													>
																														{
																															displayName
																														}
																													</span>
																												</div>

																												<div
																													style={{
																														display:
																															'flex',
																														gap: '8px'
																													}}
																												>
																													{(
																														[
																															'read',
																															'create',
																															'update'
																														] as const
																													).map(
																														(
																															act
																														) => {
																															const isParentSubsumed =
																																pc.everything ||
																																ec.everything ||
																																isWorkspaceResourceTypeActionActive(
																																	resType,
																																	act
																																) ||
																																pc
																																	.resourceTypes[
																																	resType
																																]?.[
																																	act
																																] ||
																																currentTypeConfig[
																																	act
																																];

																															return (
																																<span
																																	key={
																																		act
																																	}
																																	id={`subsumed-env-item-${pc.projectId}-${ec.envId}-${resItem.resourceId}-${act}`}
																																>
																																	<FormControlLabel
																																		control={
																																			<Checkbox
																																				size="small"
																																				checked={
																																					isParentSubsumed ||
																																					itemConfig[
																																						act
																																					]
																																				}
																																				disabled={
																																					isParentSubsumed
																																				}
																																				onChange={(
																																					e
																																				) => {
																																					const checked =
																																						e
																																							.target
																																							.checked;
																																					setProjectConfigs(
																																						(
																																							prev
																																						) =>
																																							prev.map(
																																								(
																																									item
																																								) =>
																																									item.projectId ===
																																									pc.projectId
																																										? {
																																												...item,
																																												envConfigs:
																																													item.envConfigs.map(
																																														(
																																															eItem
																																														) =>
																																															eItem.envId ===
																																															ec.envId
																																																? {
																																																		...eItem,
																																																		specificResources:
																																																			{
																																																				...eItem.specificResources,
																																																				[resItem.resourceId]:
																																																					{
																																																						...eItem
																																																							.specificResources[
																																																							resItem
																																																								.resourceId
																																																						],
																																																						[act]: checked
																																																					}
																																																			}
																																																	}
																																																: eItem
																																													)
																																											}
																																										: item
																																							)
																																					);
																																				}}
																																			/>
																																		}
																																		label={
																																			<span
																																				style={{
																																					fontSize:
																																						'10.5px',
																																					color: isParentSubsumed
																																						? '#94a3b8'
																																						: '#334155'
																																				}}
																																			>
																																				{capitalizeFirstLetter(
																																					act
																																				)}
																																			</span>
																																		}
																																	/>
																																	{isParentSubsumed && (
																																		<Tooltip
																																			anchorQuerySelector={`#subsumed-env-item-${pc.projectId}-${ec.envId}-${resItem.resourceId}-${act}`}
																																			placement="top"
																																		>
																																			Covered
																																			by
																																			higher-level
																																			scope.
																																		</Tooltip>
																																	)}
																																</span>
																															);
																														}
																													)}
																												</div>
																											</div>
																										);
																									}
																								)}
																							</div>
																						)}
																				</div>
																			);
																		}
																	)}
																</div>
															</div>
														</AccordionDetails>
													</Accordion>
												);
											})}
										</div>
									</div>
								</AccordionDetails>
							</Accordion>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default HierarchicalScopeBuilder;
